import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtN = (n, d = 2) => Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const ago = (iso) => {
  if (!iso) return null;
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  return h < 1 ? "just now" : h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

export function FxRateControl() {
  const [cfg, setCfg] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setCfg(data);
      setVal("");
      toast.success(okMsg(data));
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Could not update rate"); }
    finally { setBusy(false); }
  };
  const pin = (rate) => run(() => api.post("/payments/fx-override", { rate }), (d) => `Checkout rate pinned at ₦${fmtN(d.usd_to_ngn_rate)}/$`);
  const clear = () => run(() => api.post("/payments/fx-override", { rate: null }), (d) => `Automatic: live daily rate ₦${fmtN(d.usd_to_ngn_rate, 0)}/$`);
  const refresh = () => run(() => api.post("/payments/fx-refresh"), (d) => `Live rate refreshed: ₦${fmtN(d.live_rate, 2)}/$`);

  if (!cfg) return null;
  const manual = cfg.rate_source === "manual";
  const rate = cfg.usd_to_ngn_rate;
  return (
    <div className="mt-4 rounded-lg border border-[#27272A] bg-[#0A0A0A] p-4" data-testid="fx-rate-control">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Naira checkout rate (Paystack &amp; Flutterwave)</div>
          <div className="mt-1 font-mono font-bold text-xl tabular-nums" data-testid="fx-rate-current">
            1 USD = ₦{fmtN(rate)}
            <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${manual ? "text-[#FF9F0A] border-[#FF9F0A]/40" : cfg.rate_source === "live" ? "text-[#34C759] border-[#34C759]/40" : "text-neutral-400 border-neutral-600"}`} data-testid="fx-rate-source">{cfg.rate_source}</span>
          </div>
          <div className="text-xs text-neutral-500 mt-0.5" data-testid="fx-rate-mode">
            {manual
              ? `Manual — pinned by ${cfg.manual_set_by || "admin"} ${ago(cfg.manual_set_at) || ""}. Live market today: ₦${cfg.live_rate ? fmtN(cfg.live_rate, 0) : "—"}`
              : cfg.rate_source === "live"
                ? `Automatic — live market rate, refreshed daily at 05:00 UTC (last update ${ago(cfg.live_updated_at) || "unknown"})`
                : "Automatic — live rate not loaded yet, using fixed fallback"}
          </div>
          <div className="text-xs text-neutral-400 mt-1" data-testid="fx-rate-examples">
            $100 monthly → <span className="font-mono text-white">₦{fmtN(100 * rate, 0)}</span> · $1,000 yearly → <span className="font-mono text-white">₦{fmtN(1000 * rate, 0)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="number" min="100" step="1" value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. 1331" data-testid="fx-rate-input" className="h-9 w-32 bg-black/40 border-white/10 font-mono" />
          <Button size="sm" disabled={busy || !val} onClick={() => pin(Number(val))} data-testid="fx-rate-set" className="rounded-full bg-white text-black hover:bg-neutral-200 font-semibold">Pin manually</Button>
          {manual && <Button size="sm" variant="outline" disabled={busy} onClick={clear} data-testid="fx-rate-clear" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-white">Switch to automatic</Button>}
          <Button size="sm" variant="ghost" disabled={busy} onClick={refresh} data-testid="fx-rate-refresh" className="rounded-full text-neutral-300 hover:text-white hover:bg-white/5" title="Pull today's market rate now">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh live
          </Button>
        </div>
      </div>
    </div>
  );
}
